import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ShelterInfoProps {
  name: string;
  logo: string;
  logoAlt: string;
  location: string;
  phone: string;
  email: string;
  website: string;
  verified: boolean;
  adoptionFee: string;
}

const ShelterInfo = ({
  name,
  logo,
  logoAlt,
  location,
  phone,
  email,
  website,
  verified,
  adoptionFee,
}: ShelterInfoProps) => {
  return (
    <div className="p-6 bg-card rounded-xl border border-border space-y-6">
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
          <AppImage src={logo} alt={logoAlt} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-xl font-headline font-semibold text-foreground">{name}</h3>
            {verified && (
              <Icon name="CheckBadgeIcon" size={20} variant="solid" className="text-primary" />
            )}
          </div>
          <p className="text-sm text-muted-foreground font-body">Verified Shelter Partner</p>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-start gap-3">
          <Icon name="MapPinIcon" size={20} variant="outline" className="text-primary mt-0.5" />
          <div>
            <p className="text-sm text-muted-foreground font-body mb-1">Location</p>
            <p className="text-base text-foreground font-body">{location}</p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Icon name="PhoneIcon" size={20} variant="outline" className="text-primary mt-0.5" />
          <div>
            <p className="text-sm text-muted-foreground font-body mb-1">Phone</p>
            <a
              href={`tel:${phone}`}
              className="text-base text-primary font-body hover:underline"
            >
              {phone}
            </a>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Icon name="EnvelopeIcon" size={20} variant="outline" className="text-primary mt-0.5" />
          <div>
            <p className="text-sm text-muted-foreground font-body mb-1">Email</p>
            <a
              href={`mailto:${email}`}
              className="text-base text-primary font-body hover:underline"
            >
              {email}
            </a>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Icon name="GlobeAltIcon" size={20} variant="outline" className="text-primary mt-0.5" />
          <div>
            <p className="text-sm text-muted-foreground font-body mb-1">Website</p>
            <a
              href={website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-base text-primary font-body hover:underline"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      <div className="pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground font-body">Adoption Fee</span>
          <span className="text-2xl font-headline font-semibold text-primary">
            {adoptionFee}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ShelterInfo;